import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { useReducedMotion } from "./motionPreference";
import { DURATION } from "./tokens";

// The slide-over Sheet (release 1.4.0): one side panel for the whole site. A right-hand drawer on desktop, a bottom
// sheet on a phone. It is a real modal dialog: focus moves into it, Tab stays inside, Escape or the backdrop closes it,
// and focus returns to whatever opened it. Opening state can live in the address bar (useSheetParam) so Back closes it
// and a link can open it. Slides with transform only; with reduced motion it simply appears.

const PHONE_QUERY = "(max-width: 767px)";

function subscribePhone(listener: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => undefined;
  const mql = window.matchMedia(PHONE_QUERY);
  mql?.addEventListener?.("change", listener);
  return () => mql?.removeEventListener?.("change", listener);
}

function phoneSnapshot(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return Boolean(window.matchMedia(PHONE_QUERY)?.matches);
}

/** True below the `md` breakpoint (the phone layout). */
export function useIsPhone(): boolean {
  return useSyncExternalStore(subscribePhone, phoneSnapshot, () => false);
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Pinned under the content (actions such as "Open the full page with this filter"). */
  footer?: ReactNode;
  /** Desktop width: md ≈ 28 rem, lg ≈ 40 rem. */
  size?: "md" | "lg";
  /** On a phone, cover the whole screen instead of a bottom sheet (used by the chart drill panel). */
  phoneFullScreen?: boolean;
  /** Where focus goes when it opens (default: the close button). */
  initialFocusRef?: RefObject<HTMLElement>;
  "data-testid"?: string;
}

export function Sheet({ open, onClose, title, description, children, footer, size = "md", phoneFullScreen = false, initialFocusRef, "data-testid": testId = "sheet" }: SheetProps) {
  const reduced = useReducedMotion();
  const phone = useIsPhone();
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  // Stay mounted while the closing slide plays.
  const [rendered, setRendered] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      setClosing(false);
      return;
    }
    if (!rendered) return;
    if (reduced) {
      setRendered(false);
      return;
    }
    setClosing(true);
    const t = window.setTimeout(() => {
      setRendered(false);
      setClosing(false);
    }, DURATION.base);
    return () => window.clearTimeout(t);
  }, [open, reduced]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus in on open; back to the opener on close.
  useEffect(() => {
    if (!open) return;
    returnTo.current = (document.activeElement as HTMLElement | null) ?? null;
    const t = window.setTimeout(() => (initialFocusRef?.current ?? closeRef.current)?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      const back = returnTo.current;
      if (back && document.contains(back)) window.setTimeout(() => back.focus(), 0);
    };
  }, [open, initialFocusRef]);

  // Escape closes; Tab stays inside; the page behind does not scroll.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute("inert"));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (!panelRef.current.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!rendered || typeof document === "undefined") return null;

  const state = closing ? "closing" : "open";
  const placement = phone
    ? phoneFullScreen
      ? "inset-0 h-full max-h-none rounded-none"
      : "inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl"
    : `inset-y-0 right-0 h-full w-full ${size === "lg" ? "max-w-2xl" : "max-w-md"} rounded-l-2xl`;

  return createPortal(
    <div className="fixed inset-0 z-[70] print:hidden" data-testid={`${testId}-root`}>
      <div
        className={`motion-sheet-backdrop absolute inset-0 bg-slate-900/40 ${reduced ? "" : "motion-animate"}`}
        data-state={state}
        aria-hidden="true"
        onClick={onClose}
        data-testid={`${testId}-backdrop`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        className={`motion-sheet-panel absolute flex flex-col border border-slate-200 bg-white shadow-2xl ${placement} ${reduced ? "" : "motion-animate"}`}
        data-side={phone ? "bottom" : "right"}
        data-state={state}
        data-testid={testId}
      >
        {phone && !phoneFullScreen && <div className="mx-auto mt-2 h-1.5 w-10 flex-none rounded-full bg-slate-300" aria-hidden="true" />}
        <div className="flex flex-none items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold tracking-tight text-slate-900">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-0.5 text-xs text-slate-600">
                {description}
              </p>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="flex-none rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label="Close panel"
            onClick={onClose}
            data-testid={`${testId}-close`}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">{children}</div>
        {footer && <div className="flex flex-none flex-wrap items-center gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/**
 * Keeps a sheet's open state in the address bar (`?<name>=<value>`): opening adds a history entry so Back closes the
 * panel, and the link can be shared. Returns the current value (null when closed), an opener and a closer.
 */
export function useSheetParam(name: string): [string | null, (value: string) => void, () => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(name);
  const openSheet = useCallback(
    (v: string) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(name, v);
        return next;
      }),
    [name, setParams]
  );
  const closeSheet = useCallback(
    () =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(name);
          return next;
        },
        { replace: true }
      ),
    [name, setParams]
  );
  return [value, openSheet, closeSheet];
}
